"use client";

import { useEffect, useRef, useState } from "react";
import { turnstileWidgetKey } from "@/lib/turnstile";

type TurnstileApi = {
  render: (
    el: HTMLElement,
    options: {
      sitekey: string;
      theme: "dark";
      size: "flexible";
      callback: (token: string) => void;
      "expired-callback": () => void;
      "error-callback": () => void;
    },
  ) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

type Props = {
  action: (formData: FormData) => Promise<{ ok: boolean; error?: string }>;
  children: React.ReactNode;
  submitLabel: string;
  success: string;
  submitClassName?: string;
  footer?: React.ReactNode;
};

let turnstileScript: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (turnstileScript) return turnstileScript;
  turnstileScript = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      turnstileScript = null;
      reject(new Error("Turnstile failed to load"));
    };
    document.head.appendChild(script);
  });
  return turnstileScript;
}

export function PublicForm({ action, children, submitLabel, success, submitClassName, footer }: Props) {
  const siteKey = turnstileWidgetKey();
  const production = process.env.NODE_ENV === "production";
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [pending, setPending] = useState(false);
  const [token, setToken] = useState("");
  const hostRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef("");

  useEffect(() => {
    if (!siteKey || !hostRef.current) return;
    let cancelled = false;
    loadTurnstile()
      .then(() => {
        if (cancelled || !hostRef.current || !window.turnstile || widgetId.current) return;
        widgetId.current = window.turnstile.render(hostRef.current, {
          sitekey: siteKey,
          theme: "dark",
          size: "flexible",
          callback: (next) => setToken(next),
          "expired-callback": () => setToken(""),
          "error-callback": () => setToken(""),
        });
      })
      .catch(() => {
        if (!cancelled) setError("Verification could not load. Please refresh and try again.");
      });
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = "";
      }
    };
  }, [siteKey]);

  if (done) {
    return <p className="panel text-base">{success}</p>;
  }

  return (
    <form
      className="grid min-w-0 grid-cols-1 gap-4"
      onSubmit={async (event) => {
        event.preventDefault();
        setError(null);
        if (production && !siteKey) {
          setError("Verification is unavailable right now. Please refresh, or call the clinic.");
          return;
        }
        if (siteKey && !token) {
          setError("Please complete the verification.");
          return;
        }
        setPending(true);
        const formData = new FormData(event.currentTarget);
        const result = await action(formData);
        setPending(false);
        if (!result.ok) {
          setError(result.error || "Something went wrong.");
          setToken("");
          if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current);
          return;
        }
        setDone(true);
      }}
    >
      <div className="hidden" aria-hidden="true">
        <label>
          Website
          <input type="text" name="website" tabIndex={-1} autoComplete="off" />
        </label>
      </div>
      {children}
      {siteKey ? (
        <div className="min-w-0 max-w-full">
          <p className="mb-2 text-base">Verification</p>
          <div ref={hostRef} className="min-h-16 w-full max-w-full overflow-hidden" />
          <input type="hidden" name="captchaToken" value={token} />
        </div>
      ) : null}
      {error ? <p className="text-base text-red-300">{error}</p> : null}
      <button
        type="submit"
        disabled={pending || (production && !siteKey)}
        className={submitClassName ?? "btn w-full sm:w-auto"}
      >
        {pending ? "Sending…" : submitLabel}
      </button>
      {footer}
    </form>
  );
}
